import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Phone, User, Heart, MapPin, AlertTriangle, Loader2, ArrowRight, Package } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function PublicQR() {
  const { qrId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => {
          params.set("lat", p.coords.latitude);
          params.set("lng", p.coords.longitude);
          load(params);
        },
        () => load(params),
        { timeout: 3000 }
      );
    } else {
      load(params);
    }
    // eslint-disable-next-line
  }, [qrId]);

  const load = async (params) => {
    try {
      const { data } = await api.get(`/qr/public/${qrId}?${params.toString()}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "QR profile not found");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))] mx-auto mb-3" />
          <div className="text-sm text-muted-foreground">Loading Guardian profile...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-[hsl(var(--destructive))] mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl mb-2">Profile unavailable</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button asChild><a href="/">Back to Guardian360</a></Button>
        </div>
      </div>
    );
  }

  const isVehicle = data.type === "vehicle";
  const isPet = data.type === "pet";
  const isItem = data.type === "lostfound" || data.type === "luggage";
  const isPerson = ["person", "child", "senior"].includes(data.type);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-lg mx-auto px-5 py-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary))] grid place-items-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-black text-lg tracking-tight">Guardian360</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Verified safety profile</div>
            </div>
          </div>

          {/* Alert banner */}
          <div className="rounded-2xl bg-[hsl(var(--destructive))]/8 border border-[hsl(var(--destructive))]/25 p-4 mb-5 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[hsl(var(--destructive))] shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm">You scanned a Guardian QR code</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                If this is an emergency, please contact the person below or call local emergency services.
              </div>
            </div>
          </div>

          {/* Main card */}
          <div className="rounded-2xl border border-border/70 bg-card p-6 mb-4 card-shadow">
            <Badge className="mb-3 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/15 uppercase tracking-wider text-[10px]">
              {data.type}
            </Badge>
            <h1 className="font-display font-black text-3xl tracking-tighter mb-1">{data.display_name}</h1>
            {isVehicle && data.vehicle_number && <div className="text-muted-foreground">{data.vehicle_model} · {data.vehicle_number}</div>}
            {isPet && data.pet_breed && <div className="text-muted-foreground">{data.pet_breed}</div>}
            {data.address && (
              <div className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> {data.address}
              </div>
            )}
          </div>

          {/* Medical / details */}
          {isPerson && (data.blood_group || data.allergies || data.medical_notes) && (
            <div className="rounded-2xl border border-border/70 bg-card p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Heart className="w-4 h-4 text-[hsl(var(--destructive))]" />
                <div className="font-semibold">Medical information</div>
              </div>
              <div className="space-y-2 text-sm">
                {data.blood_group && <InfoRow label="Blood group" value={data.blood_group} />}
                {data.allergies && <InfoRow label="Allergies" value={data.allergies} />}
                {data.medical_notes && <InfoRow label="Notes" value={data.medical_notes} />}
              </div>
            </div>
          )}

          {isItem && (data.item_description || data.reward) && (
            <div className="rounded-2xl border border-border/70 bg-card p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-[hsl(var(--accent))]" />
                <div className="font-semibold">Item details</div>
              </div>
              <div className="space-y-2 text-sm">
                {data.item_description && <InfoRow label="Description" value={data.item_description} />}
                {data.reward && <InfoRow label="Reward" value={data.reward} />}
              </div>
            </div>
          )}

          {/* Owner & contacts */}
          <div className="rounded-2xl border border-border/70 bg-card p-5 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-[hsl(var(--primary))]" />
              <div className="font-semibold">Contact Guardian</div>
            </div>
            {data.owner_name && <InfoRow label="Owner" value={data.owner_name} />}
            {data.owner_phone && (
              <a href={`tel:${data.owner_phone}`} className="mt-3 block">
                <Button data-testid="call-owner-btn" className="w-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))]/90 text-white">
                  <Phone className="w-4 h-4 mr-2" /> Call {data.owner_phone}
                </Button>
              </a>
            )}
            {data.emergency_contacts?.length > 0 && (
              <div className="mt-4 space-y-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Emergency contacts</div>
                {data.emergency_contacts.map((c, i) => (
                  <a key={i} href={`tel:${c.phone}`} className="flex items-center justify-between p-3 rounded-xl bg-muted hover:bg-[hsl(var(--primary))]/10 transition-colors">
                    <div>
                      <div className="text-sm font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.relation}</div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[hsl(var(--primary))]">
                      <Phone className="w-3.5 h-3.5" /> {c.phone}
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="text-center text-xs text-muted-foreground py-6">
            Powered by <a href="/" className="text-[hsl(var(--primary))] font-medium">Guardian360 <ArrowRight className="w-3 h-3 inline" /></a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground text-xs uppercase tracking-wider">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
